import { create } from "zustand";
import toast from "react-hot-toast";
import { creativesService } from "@/feature/ads/cretives/services/creatives.service";
import type { CreateAsyncVideoCreativePayload } from "@/feature/ads/cretives/services/creatives.service";
import type { CreativeItem } from "@/feature/ads/cretives/types/creatives.types";

export type VideoGeneratingItemStatus = "generating" | "done" | "error";

export interface VideoGeneratingItem {
  id: string;
  backendId?: number;
  status: VideoGeneratingItemStatus;
  videoUrl?: string;
  baseImageUrl?: string;
  format: string;
  productName: string;
  productImageUrl?: string;
  errorMessage?: string;
}

interface VideoGenerationMeta {
  websiteId: string;
}

interface VideoGenerationState {
  items: VideoGeneratingItem[];
  startGeneration: (
    payload: CreateAsyncVideoCreativePayload,
    meta: VideoGenerationMeta
  ) => Promise<boolean>;
  clearCompleted: () => void;
  clearAll: () => void;
  removeItems: (ids: string[]) => void;
}

let jobCounter = 0;
let pollingInterval: ReturnType<typeof setInterval> | null = null;

// Video generation via n8n takes 8-10 minutes (Gemini image + Fal video + S3 upload).
// We poll every 15s for up to 80 attempts = 20 min max, which covers worst-case
// scenarios including n8n queue delays. The backend StaleCreativeCleanupJob marks
// CREATED records as FAILED after 5 min, but video records may take longer.
const POLL_INTERVAL_MS = 15_000;
const MAX_POLL_ATTEMPTS = 80;

function stopPolling() {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
}

/** Match backend creatives against local generating items and update statuses. */
function reconcileItems(
  items: VideoGeneratingItem[],
  creatives: CreativeItem[]
): VideoGeneratingItem[] {
  return items.map((item) => {
    if (item.status !== "generating" || !item.backendId) return item;

    const match = creatives.find((c) => c.id === item.backendId);
    if (match?.video_url) {
      return {
        ...item,
        status: "done" as const,
        videoUrl: match.video_url,
        baseImageUrl: match.base_image_url || undefined,
      };
    }
    if (match?.status === "FAILED") {
      return {
        ...item,
        status: "error" as const,
        errorMessage: "La generación del video falló",
      };
    }
    return item;
  });
}

/** Mark all remaining generating items as timed-out errors. */
function timeoutItems(items: VideoGeneratingItem[]): VideoGeneratingItem[] {
  return items.map((i) =>
    i.status === "generating"
      ? { ...i, status: "error" as const, errorMessage: "Tiempo de espera agotado" }
      : i
  );
}

function startPolling() {
  if (pollingInterval) return;

  let attempts = 0;

  pollingInterval = setInterval(async () => {
    attempts++;
    const { items } = useVideoGenerationStore.getState();

    const generating = items.filter((i) => i.status === "generating");
    if (generating.length === 0) {
      stopPolling();
      return;
    }

    if (attempts > MAX_POLL_ATTEMPTS) {
      stopPolling();
      useVideoGenerationStore.setState((state) => ({
        items: timeoutItems(state.items),
      }));
      return;
    }

    // Skip fetch if no items have a backend ID yet
    if (!generating.some((i) => i.backendId)) return;

    try {
      const creatives = await creativesService.listByProductId();

      useVideoGenerationStore.setState((state) => ({
        items: reconcileItems(state.items, creatives),
      }));

      const updated = useVideoGenerationStore.getState().items;
      if (!updated.some((i) => i.status === "generating")) {
        stopPolling();
      }
    } catch (err) {
      console.error("[VideoGeneration] Polling error:", err);
    }
  }, POLL_INTERVAL_MS);
}

const useVideoGenerationStore = create<VideoGenerationState>((set) => ({
  items: [],

  startGeneration: async (payload, meta) => {
    try {
      const createdCreatives =
        await creativesService.generateVideoCreativeAsync(
          meta.websiteId,
          payload
        );

      const newItems: VideoGeneratingItem[] = createdCreatives.map(
        (creative) => ({
          id: `vgen-${Date.now()}-${jobCounter++}`,
          backendId: creative.id ?? undefined,
          status: "generating" as const,
          format: creative.format || "9:16",
          productName: payload.product_name,
          productImageUrl: payload.product_image || undefined,
        })
      );

      const hasTrackableItems = newItems.some(
        (item) => item.backendId != null
      );
      if (newItems.length === 0 || !hasTrackableItems) {
        const fallbackItem: VideoGeneratingItem = {
          id: `vgen-${Date.now()}-${jobCounter++}`,
          status: "generating" as const,
          format: "9:16",
          productName: payload.product_name,
          productImageUrl: payload.product_image || undefined,
        };
        set((state) => ({
          items: [fallbackItem, ...state.items],
        }));
      } else {
        set((state) => ({
          items: [...newItems, ...state.items],
        }));
      }

      startPolling();

      return true;
    } catch (error: unknown) {
      const err = error as {
        message?: string;
        response?: { data?: any; status?: number };
      };
      console.error("[VideoGeneration] Error:", err?.message);

      const backendMessage =
        err?.response?.data?.message || err?.response?.data?.error;
      toast.error(
        backendMessage ||
          "Error al iniciar la generación de video. Intenta de nuevo."
      );
      return false;
    }
  },

  clearCompleted: () => {
    set((state) => ({
      items: state.items.filter((i) => i.status !== "done"),
    }));
    const remaining = useVideoGenerationStore.getState().items;
    if (!remaining.some((i) => i.status === "generating")) {
      stopPolling();
    }
  },

  clearAll: () => {
    set({ items: [] });
    stopPolling();
  },

  removeItems: (ids) => {
    set((state) => ({
      items: state.items.filter((i) => !ids.includes(i.id)),
    }));
  },
}));

export default useVideoGenerationStore;
